import React, { useMemo } from 'react';
import { Box, capitalizeFirstLetter, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { EMPTY_FILE_FIELD_VALUE, MINIMIZE_DATE_FORMAT, TIME_FORMAT } from '@/common/constants';
import { getAvatar, HIDDEN_UPLOADING_WAY } from '@/modules/FileManager/hooks/useFilesList';
import theme from '@/styles/theme';

import { FILE_TYPE_TITLES, FileStatus } from '../../FileManager.constants';
import { StyledIcon } from '../FileSummary.styles';
import { FileDetails } from '../queries/useGetFileDetails.query';

const getDateTime = (date?: string) => {
  if (!date) return EMPTY_FILE_FIELD_VALUE;
  const isValidDate = dayjs(date).isValid();
  if (!isValidDate) {
    return (
      <Box direction="row" align="center">
        <Inscription color="textBody" lineHeight="20px">
          {EMPTY_FILE_FIELD_VALUE}
        </Inscription>
      </Box>
    );
  }
  return (
    <Box direction="row" align="center">
      <Inscription color="textBody" lineHeight="20px">
        {dayjs(date).format(MINIMIZE_DATE_FORMAT)}
      </Inscription>
      <Box width="4px" height="4px" round background="border" margin={{ horizontal: 'spacing8' }} />
      <Inscription color="textBody" lineHeight="20px">
        {dayjs(date).format(TIME_FORMAT)}
      </Inscription>
    </Box>
  );
};

const useGetInformationFields = (fileDetails?: FileDetails) => {
  const status = useMemo(
    () => fileDetails?.fileProcessingStatusType || '',
    [fileDetails?.fileProcessingStatusType]
  );
  const isCanceled = useMemo(() => {
    return status === FileStatus.Canceled;
  }, [status]);
  const statusColor = useMemo(() => {
    if (status === FileStatus.Uploading) {
      return theme.colors.statusColors.uploading;
    }

    if (status === FileStatus.Processing) {
      return theme.colors.statusColors.processingPending;
    }

    if (status === FileStatus.Error || status === FileStatus.Duplicate || status === FileStatus.Canceled) {
      return theme.colors.statusColors.deniedErrorExpired;
    }

    if (status === FileStatus.Uploaded) {
      return theme.colors.statusColors.uploadedApproved;
    }
    if (status === FileStatus.Completed) {
      return theme.colors.statusColors.paidActiveComplete;
    }

    return theme.colors.statusColors.lockedNull;
  }, [status]);

  const statusTitle = useMemo(() => {
    return capitalizeFirstLetter(status);
  }, [status]);

  if (!fileDetails) return [];

  return [
    {
      name: 'fileName',
      label: 'File name',
      type: FieldTypes.Node,
      value: (
        <Inscription lineHeight="20px" color="textBody">
          {fileDetails.fileName || EMPTY_FILE_FIELD_VALUE}
        </Inscription>
      ),
    },
    {
      name: 'recordType',
      label: 'Record type',
      type: FieldTypes.Node,
      value: (
        <Box direction="row" align="center">
          {fileDetails.fileType && (
            <StyledIcon margin={{ right: '10px' }}>{getAvatar(fileDetails.fileType)}</StyledIcon>
          )}
          <Inscription lineHeight="20px" color="textBody">
            {fileDetails.fileType
              ? _.get(FILE_TYPE_TITLES, `[${fileDetails.fileType}]`) || EMPTY_FILE_FIELD_VALUE
              : EMPTY_FILE_FIELD_VALUE}
          </Inscription>
        </Box>
      ),
    },
    {
      name: 'partner',
      label: 'Partner',
      type: FieldTypes.Node,
      value: (
        <Inscription lineHeight="20px" color="textBody">
          {fileDetails.partners.join(', ') || EMPTY_FILE_FIELD_VALUE}
        </Inscription>
      ),
    },
    {
      name: 'employer',
      label: 'Employer',
      type: FieldTypes.Node,
      value: (
        <Inscription lineHeight="20px" color="textBody">
          {fileDetails.employers.join(', ') || EMPTY_FILE_FIELD_VALUE}
        </Inscription>
      ),
    },
    {
      name: 'DateReceived',
      label: 'Date/Time uploaded',
      type: FieldTypes.Node,
      value: getDateTime(fileDetails.createdAt),
    },
    {
      name: 'uploadedBy',
      label: 'Uploaded by',
      type: FieldTypes.Node,
      value: (
        <Box direction="row" align="center">
          <Inscription lineHeight="20px" color="textBody">
            {fileDetails.uploadedBy || EMPTY_FILE_FIELD_VALUE}
          </Inscription>
          {fileDetails.uploadWay !== HIDDEN_UPLOADING_WAY && (
            <Box background="border1" round="dropdownItemRound" pad="3px" margin={{ left: 'spacing4' }}>
              <Inscription size="10px" lineHeight="18px" weight={500} color="textBody">
                {fileDetails.uploadWay || EMPTY_FILE_FIELD_VALUE}
              </Inscription>
            </Box>
          )}
        </Box>
      ),
    },
    {
      name: 'dateProcessed',
      label: isCanceled ? 'Canceled at' : 'Date/Time processed',
      type: FieldTypes.Node,
      value: getDateTime(fileDetails.processedAt || EMPTY_FILE_FIELD_VALUE),
    },
    {
      name: 'processedBy',
      label: isCanceled ? 'Canceled by' : 'Processed By',
      type: FieldTypes.Node,
      value: (
        <Box direction="row" align="center">
          <Inscription lineHeight="20px" color="textBody">
            {fileDetails.processedBy || EMPTY_FILE_FIELD_VALUE}
          </Inscription>
        </Box>
      ),
    },
    {
      name: 'status',
      label: 'Status',
      type: FieldTypes.Node,
      value: (
        <Box direction="row" align="center">
          <Box
            margin={{ right: 'spacing8' }}
            width={{ min: '11px', max: '11px' }}
            height={{ min: '11px', max: '11px' }}
            round="50%"
            background={statusColor}
          />

          <Inscription lineHeight="20px" color="textBody">
            {statusTitle || EMPTY_FILE_FIELD_VALUE}
          </Inscription>
        </Box>
      ),
    },
  ] as Field[];
};

export default useGetInformationFields;
